import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import {
  Sparkles, Stethoscope, Smile, Activity, Shield, GraduationCap,
  Star, MapPin, Phone, Mail, Clock, ChevronDown, Check, ArrowUpRight, ShieldCheck, Lock
} from "lucide-react";
import { FaWhatsapp, FaGoogle } from "react-icons/fa";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import {
  CLINIC_WHATSAPP, CLINIC_PHONES, CLINIC_EMAIL, CLINIC_ADDRESS,
  CLINIC_HOURS, CLINIC_GEO,
  GOOGLE_BUSINESS_URL, GOOGLE_DIRECTIONS_URL, GOOGLE_REVIEW_URL, GOOGLE_MAPS_EMBED_URL,
} from "@/lib/clinic";
import { api, formatError } from "@/lib/api";

const HERO_SMILE = "/hero-smile.png";
const CLINIC_INTERIOR = "https://customer-assets.emergentagent.com/job_dentist-connect-12/artifacts/tgkfxsdq_Dental%20Clinic%201.png";
const DR_PORTRAIT = "https://customer-assets.emergentagent.com/job_dentist-connect-12/artifacts/quf359fa_Dr%20Ashish.png";
const GALLERY_SMILE = "https://static.prod-images.emergentagent.com/jobs/535ea6e2-e82a-4753-9f58-e56ceead53a9/images/aab3c0ad345a5d581b5c64e10f82010c6d397c89c68775cd3d169813b384e1ca.png";
const PROCEDURE_IMG = "https://images.unsplash.com/photo-1606811971618-4486d14f3f99?auto=format&fit=crop&w=600&q=80";
const TEEN_BRACES = "https://static.prod-images.emergentagent.com/jobs/535ea6e2-e82a-4753-9f58-e56ceead53a9/images/aab3c0ad345a5d581b5c64e10f82010c6d397c89c68775cd3d169813b384e1ca.png";

const STARS = ["s1", "s2", "s3", "s4", "s5"];

const SERVICES = [
  { icon: Activity, title: "Root Canal & Endodontics", desc: "Single-sitting RCT, conventional RCT, Re-RCT — pain-free with rotary endodontics and apex locator.", img: "/svc-rct.png", span: "md:col-span-5 md:row-span-2" },
  { icon: Smile, title: "Cosmetic Dentistry", desc: "Dental bleaching and tooth-colored material filling for a flawless, natural-looking smile.", img: "/svc-cosmetic.png", span: "md:col-span-4" },
  { icon: Sparkles, title: "Crowns, Bridges & Implants", desc: "Metal & ceramic crowns, PFM (Vita / Ivoclar), Zirconia, bridges and implant-supported dentures.", img: "/svc-crowns.png", span: "md:col-span-3" },
  { icon: Shield, title: "Periodontics & Surgery", desc: "Treatment of bleeding gums, ultrasonic scaling, flap surgery, gutka cleaning and cyst removal.", img: "/svc-gums.png", span: "md:col-span-4" },
  { icon: GraduationCap, title: "Orthodontics (Braces)", desc: "Metal, ceramic and lingual braces — alignment plans tailored to your jaw and lifestyle.", img: "/svc-braces.png", span: "md:col-span-4" },
  { icon: Stethoscope, title: "Diagnostics & Imaging", desc: "Digital X-Ray, apex locator and full diagnostic work-up performed in-house for accurate, fast decisions.", img: "/svc-pediatric.png", span: "md:col-span-4" },
];

const PRICING = [];

const TESTIMONIALS = [
  { name: "Priya Mehta", text: "Walking into Dr. Ashish's clinic felt like walking into a calm gallery, not a dental office. Painless RCT and the smile makeover gave me back my confidence.", role: "Patient · Cosmetic" },
  { name: "Rohan Verma", text: "Most precise implant work I've experienced. The 3D planning + sedation made the procedure literally a non-event.", role: "Patient · Implants" },
  { name: "Anika Sharma", text: "Took my 5-year-old here — she actually asked to come back. That tells you everything.", role: "Parent · Pediatric" },
];

const FAQS = [
  { q: "What is the consultation fee?", a: "An OPD consultation is just ₹100, and a digital X-ray is ₹200. Transparent pricing with no surprises." },
  { q: "Are you open on Sundays?", a: "Yes — we are open all 7 days, Monday to Sunday, from 9:00 AM to 8:00 PM. Walk-ins are welcome, but booking ahead via WhatsApp guarantees your slot." },
  { q: "Which languages do you speak?", a: "Dr. Ashish and the team speak Hindi, English and Bhojpuri so every patient feels at home." },
  { q: "How safe and sterile is the clinic?", a: "We follow CDC/WHO Class B autoclave sterilization, single-use barriers, and digital X-ray with low-radiation Apex Locator-assisted diagnostics." },
  { q: "Can I book on WhatsApp?", a: "Absolutely — tap the WhatsApp floating button at any time, or use the online booking flow with secure Stripe payment." },
];

export default function Home() {
  return (
    <div className="bg-clinic-bg text-clinic-ink">
      {/* HERO */}
      <section data-testid="hero-section" className="relative pt-32 pb-20 md:pt-36 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 dot-bg opacity-100 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-6"
          >
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-clinic-pine bg-white px-3 py-1.5 rounded-full border border-clinic-line shadow-sm">
              <Sparkles size={14} className="text-clinic-pine" /> Premium Dentistry · Siwan
            </div>
            <h1 className="font-heading text-[52px] leading-[0.95] sm:text-6xl lg:text-[88px] mt-7 tracking-tight font-extrabold">
              The Dentist <br />
              <span className="text-clinic-pine">You Trust</span>
            </h1>
            <p className="mt-8 max-w-md text-base md:text-lg text-clinic-muted leading-relaxed">
              We use advanced tech and a patient-first approach for superior care — gentle, evidence-led dentistry by Dr. Ashish Kumar.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/book" data-testid="hero-book-btn" className="btn-primary">
                Book Appointment
              </Link>
              <a href={`https://wa.me/${CLINIC_WHATSAPP}`} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn" className="btn-ghost">
                <FaWhatsapp /> Chat on WhatsApp
              </a>
            </div>
            <div className="mt-12 flex flex-wrap items-center gap-8">
              <div>
                <div className="font-heading text-3xl text-clinic-pine">5+</div>
                <div className="text-xs uppercase tracking-[0.18em] text-clinic-muted mt-1">Years of practice</div>
              </div>
              <div className="h-10 w-px bg-clinic-line" />
              <div>
                <div className="font-heading text-3xl text-clinic-pine">5,000+</div>
                <div className="text-xs uppercase tracking-[0.18em] text-clinic-muted mt-1">Smiles transformed</div>
              </div>
              <div className="h-10 w-px bg-clinic-line" />
              <div>
                <div className="font-heading text-3xl text-clinic-pine">4.9<span className="text-base">/5</span></div>
                <div className="text-xs uppercase tracking-[0.18em] text-clinic-muted mt-1">Avg rating</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
            className="md:col-span-6 relative h-[560px] md:h-[640px]"
          >
            {/* Main arch image - smiling woman */}
            <div className="absolute left-2 md:left-6 top-0 w-[58%] h-full">
              <div className="absolute inset-0 bg-clinic-sage rounded-[50%_50%_50%_50%/50%_50%_50%_50%] scale-95 -z-0" />
              <div className="relative arch-mask h-full w-full">
                <img src={HERO_SMILE} alt="Happy patient smiling" className="w-full h-full object-cover" />
              </div>
            </div>
            {/* Top right arch - dental procedure */}
            <div className="absolute right-0 top-4 w-[36%] h-[55%]">
              <div className="arch-mask h-full w-full shadow-xl">
                <img src={PROCEDURE_IMG} alt="Dental procedure" className="w-full h-full object-cover" />
              </div>
            </div>
            {/* Bottom right arch - teen with braces */}
            <div className="absolute right-0 bottom-0 w-[36%] h-[40%]">
              <div className="arch-mask h-full w-full shadow-xl">
                <img src={TEEN_BRACES} alt="Patient with braces" className="w-full h-full object-cover" />
              </div>
            </div>
            {/* Trust badge */}
            <div className="absolute bottom-4 left-0 md:left-8 bg-white border border-clinic-line rounded-2xl p-4 shadow-xl flex items-center gap-3 z-10">
              <img src={DR_PORTRAIT} alt="Dr. Ashish" className="w-10 h-10 rounded-full object-cover" />
              <div>
                <div className="text-sm font-bold leading-tight">Dr. Ashish Kumar</div>
                <div className="flex items-center gap-1 mt-0.5">
                  {STARS.map((id) => <Star key={id} size={11} className="fill-clinic-honey text-clinic-honey" />)}
                  <span className="text-[10px] text-clinic-muted ml-1">2,140 reviews</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-clinic-line bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-wrap items-center gap-x-10 gap-y-4 justify-between text-sm text-clinic-muted">
          <div className="inline-flex items-center gap-2"><ShieldCheck size={16} className="text-clinic-pine" /> SSL-secured booking</div>
          <div className="inline-flex items-center gap-2"><Lock size={16} className="text-clinic-pine" /> Stripe encrypted payments</div>
          <div className="inline-flex items-center gap-2"><Check size={16} className="text-clinic-pine" /> CDC Class B sterilization</div>
          <div className="inline-flex items-center gap-2"><Check size={16} className="text-clinic-pine" /> Digital records · HIPAA-aligned</div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section id="services" data-testid="services-section" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-clinic-muted">Services</div>
              <h2 className="font-heading text-4xl md:text-6xl mt-3 tracking-tight max-w-2xl">
                One clinic. <span className="italic font-light">Every</span> care your smile needs.
              </h2>
            </div>
            <p className="max-w-md text-clinic-muted">
              From the first cleaning to a full smile transformation — every procedure performed under one calm roof, by Dr. Ashish himself.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5 auto-rows-[280px]">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.title}
                  data-testid={`service-card-${i}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ delay: i * 0.08, duration: 0.6 }}
                  className={`${s.span} card-elevated overflow-hidden relative flex flex-col group hover:border-clinic-pine/40 transition-colors`}
                >
                  <div className="relative flex-1 min-h-[120px] overflow-hidden bg-clinic-sage/60">
                    <img src={s.img} alt={s.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    <div className="absolute top-3 left-3 w-10 h-10 rounded-full bg-white/95 grid place-items-center text-clinic-pine shadow-sm">
                      <Icon size={18} />
                    </div>
                  </div>
                  <div className="p-6 bg-white border-t border-clinic-line">
                    <h3 className="font-heading text-lg md:text-xl tracking-tight">{s.title}</h3>
                    <p className="mt-2 text-sm text-clinic-muted leading-relaxed">{s.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" data-testid="about-section" className="py-24 md:py-32 bg-white border-y border-clinic-line">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5 relative">
            <img src={DR_PORTRAIT} alt="Dr. Ashish Kumar" className="rounded-[28px] w-full h-[520px] object-cover border border-clinic-line" />
            <div className="absolute -right-4 bottom-8 bg-clinic-sage/60 backdrop-blur-md rounded-2xl px-5 py-4 border border-clinic-line">
              <div className="text-xs uppercase tracking-[0.18em] text-clinic-pine">Registration No.</div>
              <div className="font-heading text-sm mt-1">10672/A · Bihar Dental Council</div>
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="text-xs uppercase tracking-[0.22em] text-clinic-muted">Meet your dentist</div>
            <h2 className="font-heading text-4xl md:text-5xl mt-3 tracking-tight">Dr. Ashish Kumar — gentle hands, honest care.</h2>
            <p className="mt-6 text-clinic-muted leading-relaxed text-lg">
              An Oral &amp; Dental Surgeon with 5 years of focused practice, Dr. Ashish blends modern endodontic
              techniques with an unhurried, patient-first approach. From a single-sitting RCT to a full smile
              makeover — every treatment is rooted in evidence, explained in your language, and priced transparently.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-5">
              {[
                ["BDS | MIDA", "Qualification"],
                ["Oral & Dental Surgeon", "Specialization"],
                ["Reg No. 10672/A", "Bihar Dental Council"],
                ["Hindi · English · Bhojpuri", "Languages spoken"],
              ].map(([t, s]) => (
                <div key={t} className="border-l-2 border-clinic-pine pl-4">
                  <div className="font-medium">{t}</div>
                  <div className="text-sm text-clinic-muted">{s}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section id="gallery" data-testid="gallery-section" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.22em] text-clinic-muted">Inside the clinic</div>
            <h2 className="font-heading text-4xl md:text-5xl mt-3 tracking-tight">A clinic designed for comfort.</h2>
            <p className="mt-4 text-clinic-muted">Step inside our modern operatory — pristine, calming, and equipped with the latest sterilization &amp; imaging technology.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-5">
            <div className="md:col-span-7 relative">
              <img src={CLINIC_INTERIOR} alt="Dr. Ashish Kumar's dental clinic operatory" className="rounded-[28px] h-[360px] md:h-[520px] w-full object-cover border border-clinic-line" />
              <div className="absolute bottom-5 left-5 bg-white/90 backdrop-blur-md rounded-2xl px-4 py-3 border border-clinic-line">
                <div className="text-xs uppercase tracking-[0.18em] text-clinic-pine">Operatory 01</div>
                <div className="font-heading text-sm mt-0.5">Class B sterilization · YesDent UV chamber</div>
              </div>
            </div>
            <div className="md:col-span-5">
              <img src={GALLERY_SMILE} alt="Perfect smile result" className="rounded-[28px] h-[360px] md:h-[520px] w-full object-cover border border-clinic-line" />
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" data-testid="testimonials-section" className="py-24 md:py-32 bg-clinic-sage/30 border-y border-clinic-line">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-clinic-muted">Patient voices</div>
              <h2 className="font-heading text-4xl md:text-5xl mt-3 tracking-tight">Words from real chairs.</h2>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-1">
                {STARS.map((id) => <Star key={id} size={18} className="fill-clinic-honey text-clinic-honey" />)}
                <span className="ml-2 text-sm text-clinic-muted">4.9 · Google verified</span>
              </div>
              <a
                href={GOOGLE_BUSINESS_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="testimonials-google-link"
                className="inline-flex items-center gap-2 text-sm font-medium text-clinic-pine hover:underline"
              >
                <FaGoogle size={14} /> Read all reviews on Google
                <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                data-testid={`testimonial-${i}`}
                className="card-elevated p-7"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-1">
                    {STARS.map((id) => <Star key={id} size={14} className="fill-clinic-honey text-clinic-honey" />)}
                  </div>
                  <FaGoogle size={14} className="text-clinic-muted/60" />
                </div>
                <p className="text-clinic-ink/85 leading-relaxed">"{t.text}"</p>
                <div className="mt-6">
                  <div className="font-medium">{t.name}</div>
                  <div className="text-xs text-clinic-muted">{t.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 p-6 md:p-7 rounded-3xl bg-white border border-clinic-line flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-clinic-sage/60 grid place-items-center text-clinic-pine">
                <FaGoogle size={20} />
              </div>
              <div>
                <div className="font-heading text-lg leading-tight">Visited us recently?</div>
                <p className="text-sm text-clinic-muted mt-0.5">Your review on Google means the world — it helps neighbours find gentle dental care too.</p>
              </div>
            </div>
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="leave-google-review-btn"
              className="btn-primary text-sm whitespace-nowrap"
            >
              Leave a Google review <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" data-testid="faq-section" className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] text-clinic-muted">Questions</div>
          <h2 className="font-heading text-4xl md:text-5xl mt-3 tracking-tight">Answered before you ask.</h2>
          <Accordion type="single" collapsible className="mt-10 divide-y divide-clinic-line border-y border-clinic-line">
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`} data-testid={`faq-item-${i}`} className="border-none">
                <AccordionTrigger className="py-6 text-left font-heading text-lg md:text-xl hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-clinic-muted text-base leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CONTACT */}
      <ContactSection />

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="bg-clinic-pine text-clinic-bg rounded-[32px] p-10 md:p-16 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-8">
              <h3 className="font-heading text-3xl md:text-5xl tracking-tight">Ready to begin?</h3>
              <p className="mt-3 text-clinic-bg/80 max-w-xl">Book your first consultation — secured by Stripe, confirmed instantly, with the option to chat on WhatsApp.</p>
            </div>
            <div className="md:col-span-4 flex flex-col gap-3">
              <Link to="/book" data-testid="cta-book-btn" className="inline-flex items-center justify-center gap-2 bg-clinic-bg text-clinic-pine rounded-full px-6 py-4 font-medium hover:bg-clinic-honey hover:text-clinic-ink transition-colors">
                Book online <ArrowUpRight size={18} />
              </Link>
              <a href={`https://wa.me/${CLINIC_WHATSAPP}`} target="_blank" rel="noreferrer" data-testid="cta-whatsapp-btn" className="inline-flex items-center justify-center gap-2 border border-clinic-bg/30 text-clinic-bg rounded-full px-6 py-4 font-medium hover:bg-clinic-bg/10">
                <FaWhatsapp /> WhatsApp us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatError(err));
    } finally { setLoading(false); }
  };
  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-white border-y border-clinic-line">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="text-xs uppercase tracking-[0.22em] text-clinic-muted">Visit & Contact</div>
          <h2 className="font-heading text-4xl md:text-5xl mt-3 tracking-tight">Come say hello.</h2>
          <div className="mt-10 space-y-6">
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-full bg-clinic-sage/60 grid place-items-center text-clinic-pine shrink-0"><MapPin size={18} /></div>
              <div>
                <div className="font-medium">Clinic</div>
                <p className="text-clinic-muted text-sm leading-relaxed">{CLINIC_ADDRESS.line1},<br />{CLINIC_ADDRESS.line2}<br /><span className="text-clinic-muted/80">({CLINIC_ADDRESS.landmark})</span></p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-full bg-clinic-sage/60 grid place-items-center text-clinic-pine shrink-0"><Phone size={18} /></div>
              <div>
                <div className="font-medium">Call / WhatsApp</div>
                <div className="text-clinic-muted text-sm space-y-0.5 mt-0.5">
                  {CLINIC_PHONES.map((p) => (
                    <a key={p.tel} href={`tel:${p.tel}`} className="block hover:text-clinic-pine">{p.display}</a>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-full bg-clinic-sage/60 grid place-items-center text-clinic-pine shrink-0"><Mail size={18} /></div>
              <div>
                <div className="font-medium">Email</div>
                <p className="text-clinic-muted text-sm">{CLINIC_EMAIL}</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-full bg-clinic-sage/60 grid place-items-center text-clinic-pine shrink-0"><Clock size={18} /></div>
              <div>
                <div className="font-medium">Hours</div>
                <p className="text-clinic-muted text-sm leading-relaxed">Open all 7 days<br />{CLINIC_HOURS.days} · {CLINIC_HOURS.time}</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-full bg-clinic-sage/60 grid place-items-center text-clinic-pine shrink-0"><Sparkles size={18} /></div>
              <div>
                <div className="font-medium">Languages</div>
                <p className="text-clinic-muted text-sm">Hindi · English · Bhojpuri</p>
              </div>
            </div>
          </div>
          <a href={GOOGLE_BUSINESS_URL} target="_blank" rel="noreferrer" data-testid="map-link" className="mt-8 block rounded-2xl overflow-hidden border border-clinic-line group">
            <iframe
              title="Clinic location"
              src={GOOGLE_MAPS_EMBED_URL}
              className="w-full h-[240px] border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div className="px-4 py-3 bg-white flex items-center justify-between">
              <span className="text-sm text-clinic-ink/85 inline-flex items-center gap-2"><FaGoogle size={13} /> Find us on Google</span>
              <ArrowUpRight size={16} className="text-clinic-pine group-hover:translate-x-0.5 transition" />
            </div>
          </a>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <a
              href={GOOGLE_DIRECTIONS_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="get-directions-btn"
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-clinic-line bg-white px-4 py-3 text-sm font-medium text-clinic-ink hover:border-clinic-pine hover:text-clinic-pine transition"
            >
              <MapPin size={15} /> Get directions
            </a>
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-google-review-btn"
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-clinic-line bg-white px-4 py-3 text-sm font-medium text-clinic-ink hover:border-clinic-pine hover:text-clinic-pine transition"
            >
              <FaGoogle size={13} /> Write a review
            </a>
          </div>
        </div>
        <form onSubmit={submit} className="md:col-span-7 card-elevated p-8 md:p-10" data-testid="contact-form">
          <h3 className="font-heading text-2xl tracking-tight">Send a message</h3>
          <p className="text-sm text-clinic-muted mt-1">We typically reply within a few hours.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <input data-testid="contact-name" required value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} placeholder="Your name" className="w-full px-4 py-3 rounded-xl border border-clinic-line bg-white focus:outline-none focus:border-clinic-pine" />
            <input data-testid="contact-email" required type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} placeholder="Email" className="w-full px-4 py-3 rounded-xl border border-clinic-line bg-white focus:outline-none focus:border-clinic-pine" />
            <input data-testid="contact-phone" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} placeholder="Phone (optional)" className="md:col-span-2 w-full px-4 py-3 rounded-xl border border-clinic-line bg-white focus:outline-none focus:border-clinic-pine" />
            <textarea data-testid="contact-message" required value={form.message} onChange={(e)=>setForm({...form,message:e.target.value})} placeholder="How can we help?" rows={5} className="md:col-span-2 w-full px-4 py-3 rounded-xl border border-clinic-line bg-white focus:outline-none focus:border-clinic-pine resize-none" />
          </div>
          <button disabled={loading} data-testid="contact-submit" type="submit" className="btn-primary mt-6">
            {loading ? "Sending..." : "Send message"} <ArrowUpRight size={18} />
          </button>
        </form>
      </div>
    </section>
  );
}
